"use client";

import { useEffect, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { Section } from "../primitives";
import { SectionHeader } from "../shared/SectionHeader";
import styles from "./CaseStudySlider.module.css";
import type { CaseStudyItem, CaseStudySliderProps } from "./CaseStudySlider.types";

const MAX = 6;

function ArrowUpRight(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7 17L17 7M8 7h9v9" />
    </svg>
  );
}

/**
 * Un cas client par écran, en trois colonnes : la photo, la carte jaune qui
 * porte le titre et le récit, puis deux chiffres clés en carrés au-dessus
 * d'une grande carte. Flèches sous le titre, glissement au doigt, compteur.
 * Le défilement est natif (scroll-snap) : rien à couper en mouvement réduit.
 */
export function CaseStudySlider({
  badge,
  title,
  titleAs = "h2",
  intro,
  cta,
  headerLayout = "split",
  items,
  tone = "dark",
  id,
}: CaseStudySliderProps) {
  const shown = items.slice(0, MAX);
  const trackRef = useRef<HTMLUListElement>(null);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const view = track.ownerDocument.defaultView;
    const reduced = view?.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    const slideWidth = (): number => track.querySelector<HTMLElement>("li")?.offsetWidth ?? track.clientWidth;
    const gap = (): number => Number.parseFloat(view?.getComputedStyle(track).columnGap ?? "0") || 0;
    const current = (): number => Math.round(track.scrollLeft / (slideWidth() + gap()));
    const go = (target: number): void => {
      const count = track.children.length;
      const next = ((target % count) + count) % count; // boucle dans les deux sens
      track.scrollTo({ left: next * (slideWidth() + gap()), behavior: reduced ? "auto" : "smooth" });
    };
    const onScroll = (): void => setIndex(current());
    const prev = track.parentElement?.querySelector<HTMLButtonElement>("[data-cases-prev]");
    const next = track.parentElement?.querySelector<HTMLButtonElement>("[data-cases-next]");
    const onPrev = (): void => go(current() - 1);
    const onNext = (): void => go(current() + 1);
    prev?.addEventListener("click", onPrev);
    next?.addEventListener("click", onNext);
    track.addEventListener("scroll", onScroll, { passive: true });
    // au redimensionnement, on recale sur le cas courant (la largeur d'une diapositive a changé)
    let known = current();
    const onResize = (): void => {
      track.scrollTo({ left: known * (slideWidth() + gap()), behavior: "auto" });
    };
    const remember = (): void => {
      known = current();
    };
    track.addEventListener("scrollend", remember);
    view?.addEventListener("resize", onResize, { passive: true });
    return () => {
      track.removeEventListener("scrollend", remember);
      view?.removeEventListener("resize", onResize);
      prev?.removeEventListener("click", onPrev);
      next?.removeEventListener("click", onNext);
      track.removeEventListener("scroll", onScroll);
    };
  }, [shown.length]);

  const pad = (n: number): string => String(n).padStart(2, "0");
  // Un seul cas : les flèches restent en place (le bloc garde sa silhouette), mais ne font rien.
  const single = shown.length < 2;

  const nav = (
    <div className={styles.nav}>
      <span className={styles.counter} data-cases-counter="true" aria-live="polite">
        {pad(index + 1)} / {pad(shown.length)}
      </span>
      <button type="button" className={styles.navButton} data-cases-prev="true" aria-label="Cas précédent" disabled={single || undefined}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M15 5l-7 7 7 7" />
        </svg>
      </button>
      <button type="button" className={styles.navButton} data-cases-next="true" aria-label="Cas suivant" disabled={single || undefined}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );

  const featureBody = (item: CaseStudyItem): ReactNode => (
    <>
      {item.href ? (
        <span className={styles.corner} aria-hidden="true">
          <ArrowUpRight />
        </span>
      ) : null}
      <span className={styles.featureCopy}>
        {item.stars ? (
          <span className={styles.stars} aria-label="Cinq étoiles sur cinq">
            {[0, 1, 2, 3, 4].map((star) => (
              <svg key={star} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2.5l2.9 6.2 6.8.8-5 4.7 1.3 6.8L12 17.7 6 21l1.3-6.8-5-4.7 6.8-.8z" />
              </svg>
            ))}
          </span>
        ) : null}
        {item.kicker ? <span className={styles.kicker}>{item.kicker}</span> : null}
        <span className={styles.featureTitle}>{item.title}</span>
        <span className={styles.featureText}>{item.text}</span>
      </span>
    </>
  );

  const cardBody = (item: CaseStudyItem): ReactNode => (
    <>
      {item.card.href ? (
        <span className={styles.corner} aria-hidden="true">
          <ArrowUpRight />
        </span>
      ) : null}
      <span className={styles.cardCopy}>
        {item.card.value ? <span className={styles.cardValue}>{item.card.value}</span> : null}
        <span className={styles.cardTitle}>{item.card.title}</span>
        {item.card.text ? <span className={styles.cardText}>{item.card.text}</span> : null}
      </span>
    </>
  );

  return (
    <Section tone={tone} id={id} className={styles.section}>
      <div className={styles.head}>
        <SectionHeader badge={badge} title={title} titleAs={titleAs} intro={intro} cta={cta} after={nav} layout={headerLayout} />
      </div>
      <ul ref={trackRef} className={styles.track} data-cases-track="true">
        {shown.map((item, i) => (
          <li key={i} className={styles.slide} data-slide={i}>
            <figure className={styles.photo}>
              {/* classe doublée : le CSS capturé impose `.breakdance img { height: auto }` */}
              <img className={`${styles.image} ${styles.image}`} src={item.image.src} alt={item.image.alt} loading={i === 0 ? "eager" : "lazy"} data-grayscale={item.image.grayscale || undefined} />
            </figure>
            {item.href ? (
              <a className={styles.feature} href={item.href} data-feature="true">
                {featureBody(item)}
              </a>
            ) : (
              <div className={styles.feature} data-feature="true">
                {featureBody(item)}
              </div>
            )}
            <div className={styles.aside}>
              <div className={styles.stats} data-count={item.stats.length}>
                {item.stats.map((stat, s) => (
                  <div key={s} className={styles.stat} data-stat="true">
                    <span className={styles.statValue}>{stat.value}</span>
                    <span className={styles.statLabel}>{stat.label}</span>
                  </div>
                ))}
              </div>
              {item.card.href ? (
                <a className={styles.card} href={item.card.href} data-card="true">
                  {cardBody(item)}
                </a>
              ) : (
                <div className={styles.card} data-card="true">
                  {cardBody(item)}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Section>
  );
}
